import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Send, Plus, ChevronUp, ChevronDown, X, Search, Trash2, BookOpen, ClipboardList } from 'lucide-react'
import { courseApi, lessonApi } from '../api/lessonBuilderApi'
import { countCards } from '../lib/sections'
import { compressImageFile } from '../../../utils/imageCompress'
import FileDropzone from '../components/FileDropzone'
import { COURSE_LEVELS, GRADE_OPTIONS, REVIEW_PRESETS, flattenChapters, ensureChapters, type CourseDoc, type CourseLevel, type CourseGrade, type CourseChapter, type CourseChapterKind } from '../types/CourseDoc'
import type { LessonDoc } from '../types/LessonDoc'
import { genId } from '../lib/id'

const IMG_ACCEPT = { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif'] }

// Trình soạn thảo danh sách gạch đầu dòng (Bạn sẽ học được gì / Yêu cầu).
const ListEditor: React.FC<{
  items: string[]
  placeholder: string
  onChange: (items: string[]) => void
}> = ({ items, placeholder, onChange }) => {
  const set = (i: number, v: string) => onChange(items.map((x, k) => (k === i ? v : x)))
  const add = () => onChange([...items, ''])
  const remove = (i: number) => onChange(items.filter((_, k) => k !== i))
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
          <input
            className="flex-1 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
            value={it}
            placeholder={placeholder}
            onChange={(e) => set(i, e.target.value)}
          />
          <button onClick={() => remove(i)} className="p-1 text-gray-300 hover:text-red-600">
            <X size={15} />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700"
      >
        <Plus size={14} /> Thêm dòng
      </button>
    </div>
  )
}

// Trang soạn khoá học: thông tin khoá + cài đặt chuyên nghiệp + chọn/sắp xếp bài giảng.
const CourseBuilderPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState<CourseDoc | null>(null)
  const [allLessons, setAllLessons] = useState<LessonDoc[]>([])
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [coverBusy, setCoverBusy] = useState(false)
  const [search, setSearch] = useState('')
  const [addingTo, setAddingTo] = useState<string | null>(null)   // chương đang mở danh sách thêm bài
  const [reviewMenu, setReviewMenu] = useState(false)             // dropdown thêm mục Ôn tập

  useEffect(() => {
    if (id) courseApi.get(id).then(setCourse)
    lessonApi.list().then(setAllLessons)
  }, [id])

  const lessonById = useMemo(() => new Map(allLessons.map((l) => [l.id, l] as const)), [allLessons])
  const usedIds = useMemo(() => new Set(flattenChapters(course?.chapters)), [course?.chapters])
  const availableLessons = useMemo(() => {
    if (!course) return []
    const q = search.trim().toLowerCase()
    return allLessons
      .filter((l) => !usedIds.has(l.id))
      .filter((l) => !q || (l.meta.tieuDe || '').toLowerCase().includes(q))
  }, [course, allLessons, usedIds, search])

  // Khoá cũ (chỉ có lessonIds) → tạo 1 chương mặc định để soạn phân cấp.
  useEffect(() => {
    setCourse((c) => {
      if (!c || c.chapters) return c
      const chs = ensureChapters(c)
      return { ...c, chapters: chs, lessonIds: flattenChapters(chs) }
    })
  }, [course?.id])

  if (!course) {
    return <div className="p-10 text-center text-gray-400">Không tìm thấy khoá học.</div>
  }

  const update = (patch: Partial<CourseDoc>) => setCourse({ ...course, ...patch })
  const chapters = course.chapters || []
  // lessonIds LUÔN = nối các chương (giữ phẳng cho viewer/tiến độ/thống kê).
  const setChapters = (next: CourseChapter[]) => setCourse({ ...course, chapters: next, lessonIds: flattenChapters(next) })
  const totalCards = flattenChapters(chapters).reduce((s, id) => { const l = lessonById.get(id); return s + (l ? countCards(l) : 0) }, 0)

  const addChapter = (kind: CourseChapterKind = 'chapter', title?: string) =>
    setChapters([...chapters, {
      id: genId(), kind, lessonIds: [],
      title: title || `Chương ${chapters.filter((c) => c.kind !== 'review').length + 1}`,
    }])
  const renameChapter = (cid: string, title: string) => setChapters(chapters.map((c) => (c.id === cid ? { ...c, title } : c)))
  const removeChapter = (cid: string) => {
    const c = chapters.find((x) => x.id === cid)
    if ((c?.lessonIds.length || 0) === 0 || confirm('Xoá mục này? Các bài chỉ bị gỡ khỏi khoá (KHÔNG xoá bài giảng).'))
      setChapters(chapters.filter((x) => x.id !== cid))
  }
  const moveChapter = (cid: string, dir: -1 | 1) => {
    const i = chapters.findIndex((c) => c.id === cid); const j = i + dir
    if (i < 0 || j < 0 || j >= chapters.length) return
    const n = [...chapters];[n[i], n[j]] = [n[j], n[i]]; setChapters(n)
  }
  const addLessonTo = (cid: string, lid: string) =>
    setChapters(chapters.map((c) => (c.id === cid ? { ...c, lessonIds: [...c.lessonIds, lid] } : c)))
  const removeLessonFrom = (cid: string, lid: string) =>
    setChapters(chapters.map((c) => (c.id === cid ? { ...c, lessonIds: c.lessonIds.filter((x) => x !== lid) } : c)))
  const moveLessonInChapter = (cid: string, lid: string, dir: -1 | 1) =>
    setChapters(chapters.map((c) => {
      if (c.id !== cid) return c
      const ids = [...c.lessonIds]; const i = ids.indexOf(lid); const j = i + dir
      if (j < 0 || j >= ids.length) return c
      ;[ids[i], ids[j]] = [ids[j], ids[i]]; return { ...c, lessonIds: ids }
    }))

  const handleSave = async (publish: boolean) => {
    if (!course.title.trim()) {
      alert('Vui lòng nhập tên khoá học.')
      return
    }
    if (publish && course.lessonIds.length === 0) {
      alert('Hãy thêm ít nhất 1 bài giảng trước khi xuất bản.')
      return
    }
    try {
      // Khi còn nháp: gửi slug='' để backend sinh lại slug (không dấu) theo TÊN hiện tại.
      // Đã xuất bản thì giữ nguyên slug (URL công khai đã chia sẻ).
      const payload = course.status === 'published' ? course : { ...course, slug: '' }
      const saved = await courseApi.save(payload)
      setCourse(saved)
      setSavedAt(new Date().toLocaleTimeString('vi-VN'))
      if (publish) {
        const pub = await courseApi.publish(saved.id)
        navigate(`/khoa-hoc/${(pub || saved).slug}`)
      }
    } catch (e) {
      alert('Lưu khoá học THẤT BẠI (server chưa nhận):\n' + ((e as Error)?.message || e) +
        '\n\nKhoá CHƯA được lưu. Hãy chụp lỗi này gửi hỗ trợ.')
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/teacher/khoa-hoc')} className="text-gray-400 hover:text-gray-700">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-black text-gray-900">Soạn khoá học</h1>
          {savedAt && <span className="text-xs text-gray-400">Đã lưu lúc {savedAt}</span>}
        </div>
        <div className="flex items-center gap-2">
          {id && (
            <button
              onClick={() => navigate(`/bai-tap?course=${id}`)}
              title="Xem & chấm bài tập tự luận của khoá này"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100"
            >
              <ClipboardList size={16} /> Chấm bài
            </button>
          )}
          <button
            onClick={() => handleSave(false)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200"
          >
            <Save size={16} /> Lưu nháp
          </button>
          <button
            onClick={() => handleSave(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700"
          >
            <Send size={16} /> Xuất bản
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 max-w-6xl mx-auto">
        {/* Thông tin & cài đặt khoá */}
        <section className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Thông tin khoá học</h3>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Tên khoá học</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.title}
                onChange={(e) => update({ title: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Mô tả ngắn</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.subtitle || ''}
                onChange={(e) => update({ subtitle: e.target.value })}
                placeholder="1 câu tóm tắt khoá học"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Lớp (phân loại catalog)</label>
              <select
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                value={course.grade || ''}
                onChange={(e) => update({ grade: (e.target.value || undefined) as CourseGrade | undefined })}
              >
                <option value="">— Tự nhận theo tên/chủ đề —</option>
                {GRADE_OPTIONS.map((g) => (<option key={g.value} value={g.value}>{g.label}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Chủ đề / Lớp</label>
                <input
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                  value={course.category || ''}
                  onChange={(e) => update({ category: e.target.value })}
                  placeholder="Vd: Vật lí 11"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Trình độ</label>
                <select
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                  value={course.level || ''}
                  onChange={(e) => update({ level: (e.target.value || '') as CourseLevel | '' })}
                >
                  <option value="">— Chọn —</option>
                  {COURSE_LEVELS.map((lv) => (
                    <option key={lv.value} value={lv.value}>
                      {lv.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Quyền truy cập</label>
              <select
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                value={course.access || 'free'}
                onChange={(e) => update({ access: (e.target.value === 'pro' ? 'pro' : 'free') })}
              >
                <option value="free">Miễn phí (ai cũng ghi danh được)</option>
                {/* TẠM ẨN — sẽ lên kế hoạch thương mại bài giảng sau:
                <option value="pro">Yêu cầu tài khoản PRO</option> */}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Hạn hoàn thành <span className="text-gray-400 font-normal normal-case">(tuỳ chọn)</span></label>
              <input
                type="date"
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                value={course.dueDate || ''}
                onChange={(e) => update({ dueDate: e.target.value })}
              />
              <p className="text-[11px] text-gray-400 mt-1">Học viên sẽ thấy hạn & được nhắc khi sắp tới hạn. Để trống = không giới hạn.</p>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Giới thiệu chi tiết</label>
              <textarea
                className="w-full min-h-[90px] p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.description || ''}
                onChange={(e) => update({ description: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Ảnh bìa khoá học</label>
              {course.coverImage ? (
                <div className="relative mt-1 group">
                  <img src={course.coverImage} alt="Ảnh bìa" className="w-full h-36 object-cover rounded-xl border border-gray-200" />
                  <button
                    onClick={() => update({ coverImage: undefined })}
                    className="absolute top-2 right-2 px-2 py-1 rounded-lg bg-white/90 text-red-600 text-xs font-bold shadow hover:bg-white"
                  >
                    Xoá ảnh
                  </button>
                </div>
              ) : (
                <FileDropzone
                  accept={IMG_ACCEPT}
                  busy={coverBusy}
                  label="Kéo-thả ảnh bìa"
                  hint="PNG, JPG, WebP — nên dùng ảnh ngang (16:9)"
                  onFile={async (file) => {
                    setCoverBusy(true)
                    try {
                      update({ coverImage: await compressImageFile(file, { maxWidth: 1600, maxHeight: 900 }) })
                    } finally {
                      setCoverBusy(false)
                    }
                  }}
                />
              )}
              <input
                className="w-full mt-2 p-2 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-200"
                value={course.coverImage && !course.coverImage.startsWith('data:') ? course.coverImage : ''}
                onChange={(e) => update({ coverImage: e.target.value || undefined })}
                placeholder="hoặc dán URL ảnh bìa"
              />
            </div>
          </div>

          {/* Giảng viên */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Giảng viên</h3>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Tên giảng viên</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.instructorName || ''}
                onChange={(e) => update({ instructorName: e.target.value })}
                placeholder="Vd: Thầy Nguyễn Văn A"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Trường / Đơn vị</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.instructorSchool || ''}
                onChange={(e) => update({ instructorSchool: e.target.value })}
                placeholder="Vd: THPT Chuyên ..."
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Kinh nghiệm / Giới thiệu</label>
              <textarea
                className="w-full min-h-[70px] p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.instructorExperience || ''}
                onChange={(e) => update({ instructorExperience: e.target.value })}
                placeholder="Vd: 10 năm luyện thi HSG, đạt..."
              />
            </div>
          </div>

          {/* Bạn sẽ học được gì */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Bạn sẽ học được gì</h3>
            <ListEditor
              items={course.outcomes || []}
              placeholder="Vd: Hiểu định luật bảo toàn năng lượng"
              onChange={(outcomes) => update({ outcomes })}
            />
          </div>

          {/* Yêu cầu đầu vào */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Yêu cầu đầu vào</h3>
            <ListEditor
              items={course.requirements || []}
              placeholder="Vd: Nắm vững kiến thức Vật lí 10"
              onChange={(requirements) => update({ requirements })}
            />
          </div>
        </section>

        {/* Nội dung khoá học theo CHƯƠNG / mục Ôn tập */}
        <section className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">
                Nội dung khoá học ({course.lessonIds.length} bài · {chapters.length} mục · {totalCards} mục ND)
              </h3>
              <div className="flex items-center gap-2">
                <button onClick={() => addChapter('chapter')} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-600 text-xs font-bold hover:bg-blue-100"><Plus size={14} /> Chương</button>
                <div className="relative">
                  <button onClick={() => setReviewMenu((v) => !v)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-600 text-xs font-bold hover:bg-amber-100"><Plus size={14} /> Ôn tập ▾</button>
                  {reviewMenu && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setReviewMenu(false)} />
                      <div className="absolute right-0 z-30 mt-1 w-52 bg-white rounded-xl border border-gray-200 shadow-xl p-1">
                        {REVIEW_PRESETS.map((p) => (
                          <button key={p.key} onClick={() => { addChapter('review', p.title); setReviewMenu(false) }}
                            className="w-full text-left px-3 py-2 rounded-lg hover:bg-amber-50 text-sm font-semibold text-gray-700">📘 {p.title}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {chapters.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">Chưa có chương. Bấm "+ Chương" để bắt đầu.</p>
            ) : (
              <div className="space-y-3">
                {chapters.map((ch, ci) => {
                  const isReview = ch.kind === 'review'
                  return (
                    <div key={ch.id} className={`rounded-xl border ${isReview ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200'}`}>
                      {/* Header chương / mục ôn tập */}
                      <div className="flex items-center gap-2 p-2.5 border-b border-gray-100">
                        <span className="text-base shrink-0">{isReview ? '📘' : '📂'}</span>
                        <input value={ch.title} onChange={(e) => renameChapter(ch.id, e.target.value)}
                          className="flex-1 min-w-0 bg-transparent font-bold text-gray-800 text-sm outline-none border-b border-transparent focus:border-blue-200" />
                        {isReview && <span className="text-[10px] font-bold text-amber-600 bg-amber-100 rounded-full px-2 py-0.5 shrink-0">Ôn tập</span>}
                        <span className="text-[11px] text-gray-400 shrink-0">{ch.lessonIds.length} bài</span>
                        <button onClick={() => moveChapter(ch.id, -1)} disabled={ci === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronUp size={15} /></button>
                        <button onClick={() => moveChapter(ch.id, 1)} disabled={ci === chapters.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronDown size={15} /></button>
                        <button onClick={() => removeChapter(ch.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
                      </div>
                      {/* Bài trong chương */}
                      <div className="p-2.5 space-y-1.5">
                        {ch.lessonIds.length === 0 ? (
                          <p className="text-xs text-gray-400 py-1.5 text-center">Chưa có bài trong mục này.</p>
                        ) : ch.lessonIds.map((lid, li) => {
                          const l = lessonById.get(lid)
                          return (
                            <div key={lid} className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 bg-white">
                              <span className="w-5 h-5 rounded bg-blue-50 text-blue-600 text-[11px] font-bold flex items-center justify-center shrink-0">{li + 1}</span>
                              <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 truncate">{l ? l.meta.tieuDe : <span className="text-red-400 italic">(bài đã xoá)</span>}</span>
                              {l && <span className="text-[11px] text-gray-400 shrink-0">{countCards(l)} mục</span>}
                              <button onClick={() => moveLessonInChapter(ch.id, lid, -1)} disabled={li === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronUp size={14} /></button>
                              <button onClick={() => moveLessonInChapter(ch.id, lid, 1)} disabled={li === ch.lessonIds.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronDown size={14} /></button>
                              <button onClick={() => removeLessonFrom(ch.id, lid)} className="p-1 text-gray-400 hover:text-red-600"><X size={14} /></button>
                            </div>
                          )
                        })}
                        {/* Thêm bài vào chương này */}
                        {addingTo === ch.id ? (
                          <div className="mt-1 rounded-lg border border-blue-200 bg-blue-50/40 p-2">
                            <div className="relative mb-2">
                              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300" />
                              <input autoFocus className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-sm outline-none"
                                value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm bài theo tên…" />
                            </div>
                            {availableLessons.length === 0 ? (
                              <p className="text-xs text-gray-400 py-2 text-center">{search.trim() ? 'Không tìm thấy bài.' : 'Hết bài chưa dùng. Tạo bài ở "Bài giảng của tôi".'}</p>
                            ) : (
                              <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
                                {availableLessons.map((l) => (
                                  <button key={l.id} onClick={() => addLessonTo(ch.id, l.id)}
                                    className="w-full flex items-center gap-2 p-1.5 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-white text-left">
                                    <BookOpen size={14} className="text-gray-300 shrink-0" />
                                    <span className="flex-1 text-sm font-medium text-gray-800 truncate">{l.meta.tieuDe}</span>
                                    <Plus size={14} className="text-blue-600 shrink-0" />
                                  </button>
                                ))}
                              </div>
                            )}
                            <button onClick={() => { setAddingTo(null); setSearch('') }} className="mt-1.5 text-[11px] font-bold text-gray-400 hover:text-gray-700">Đóng</button>
                          </div>
                        ) : (
                          <button onClick={() => { setAddingTo(ch.id); setSearch('') }}
                            className="mt-1 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 border-dashed border-gray-200 text-gray-500 text-xs font-bold hover:border-blue-300 hover:text-blue-600">
                            <Plus size={14} /> Thêm bài vào {isReview ? 'mục ôn tập' : 'chương'} này
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default CourseBuilderPage
